/**
 * Parse a pasted or uploaded participant sheet for the direct-entry bulk
 * add. A name is all a row needs; email, job title and years of experience
 * are optional, so a sheet of names alone enrolls people who will never sign
 * in.
 *
 * Accepts what spreadsheets actually export:
 *   - with a header row, columns in any order ("Name", "Email",
 *     "Job title", "Years of experience" and close variants), or
 *   - without one, columns positional: name, email, job title, years —
 *     a single column is just names;
 *   - comma, semicolon or tab separated, with "quoted, fields".
 */

export interface ParticipantRow {
  full_name: string;
  email?: string;
  title?: string;
  years_of_experience?: number;
}

export interface ParticipantSheet {
  rows: ParticipantRow[];
  /** True when the first line was read as column names. */
  hadHeader: boolean;
}

type Field = keyof ParticipantRow;

const HEADER_ALIASES: Record<Field, string[]> = {
  full_name: ['name', 'full name', 'fullname', 'participant', 'participant name'],
  email: ['email', 'e-mail', 'email address', 'mail'],
  title: ['title', 'job title', 'role', 'position'],
  years_of_experience: ['years', 'years of experience', 'experience', 'yoe'],
};

const POSITIONAL: Field[] = ['full_name', 'email', 'title', 'years_of_experience'];

function delimiterOf(line: string): string {
  if (line.includes('\t')) return '\t';
  // A semicolon sheet (European Excel) has more ; than , on its first line.
  const semis = (line.match(/;/g) ?? []).length;
  const commas = (line.match(/,/g) ?? []).length;
  return semis > commas ? ';' : ',';
}

/** Split one CSV line, honouring "double quoted" fields and "" escapes. */
export function splitCsvLine(line: string, delim: string): string[] {
  const out: string[] = [];
  let cur = '';
  let quoted = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (quoted) {
      if (ch === '"' && line[i + 1] === '"') {
        cur += '"';
        i += 1;
      } else if (ch === '"') {
        quoted = false;
      } else {
        cur += ch;
      }
    } else if (ch === '"') {
      quoted = true;
    } else if (ch === delim) {
      out.push(cur.trim());
      cur = '';
    } else {
      cur += ch;
    }
  }
  out.push(cur.trim());
  return out;
}

function headerMap(cells: string[]): (Field | null)[] | null {
  const norm = cells.map((c) => c.toLowerCase().replace(/[_*]/g, ' ').replace(/\s+/g, ' ').trim());
  const mapped = norm.map((c) => {
    for (const [field, aliases] of Object.entries(HEADER_ALIASES)) {
      if (aliases.includes(c)) return field as Field;
    }
    return null;
  });
  // A header must name the name column; otherwise the first row is data.
  return mapped.includes('full_name') ? mapped : null;
}

function toRow(cells: string[], fields: (Field | null)[]): ParticipantRow {
  const row: ParticipantRow = { full_name: '' };
  fields.forEach((field, i) => {
    const value = (cells[i] ?? '').trim();
    if (!field || !value) return;
    if (field === 'years_of_experience') {
      const n = Number.parseInt(value, 10);
      if (Number.isFinite(n) && n >= 0) row.years_of_experience = n;
    } else {
      row[field] = value;
    }
  });
  return row;
}

export function parseParticipantSheet(text: string): ParticipantSheet {
  const lines = text
    .replace(/\r\n?/g, '\n')
    .split('\n')
    .filter((l) => l.trim() !== '');
  if (lines.length === 0) return { rows: [], hadHeader: false };
  const delim = delimiterOf(lines[0]);
  const first = splitCsvLine(lines[0], delim);
  const header = headerMap(first);
  const fields = header ?? POSITIONAL;
  const body = header ? lines.slice(1) : lines;
  const rows = body
    .map((l) => toRow(splitCsvLine(l, delim), fields))
    // A row whose every cell was empty (",,," from a spreadsheet) is noise.
    .filter((r) => r.full_name || r.email || r.title || r.years_of_experience !== undefined);
  return { rows, hadHeader: header !== null };
}
